import {
  AlteracaoAuditoriaDto,
  CategoriaAuditoria,
  ContextoAuditoriaDto,
  TipoEntidadeAuditoria
} from 'src/dto'

export interface HistoricoAuditoriaRef {
  recarregar: () => Promise<void>
}

export interface OpcaoCategoriaAuditoria {
  categoria: CategoriaAuditoria
  texto: string
}

export interface HistoricoAuditoriaProps {
  tipoEntidade: TipoEntidadeAuditoria
  entidadeId: string
  /** Quando informado, exibe o filtro por categoria com estas opções. */
  opcoesCategoria?: OpcaoCategoriaAuditoria[]
  /**
   * Mostra só esta categoria (sem filtro na tela). Usado onde o histórico é de um tipo só, como o
   * histórico financeiro do Painel Financeiro.
   */
  categoriaFixa?: CategoriaAuditoria
  /**
   * Esconde a descrição do contexto (ex.: a turma da matrícula) quando a tela já é da própria entidade,
   * mas mantém as alterações e a observação.
   */
  semContexto?: boolean
  tamanhoPagina?: number
  /**
   * Exibe só quem fez e quando (ex.: "Precificação criada" / "Precificação editada"): sem o contexto
   * (a tela já é da própria entidade) e sem os detalhes da alteração. A nota de início do registro continua.
   */
  resumido?: boolean
  /**
   * Chamado depois de carregar a primeira página, com a quantidade de itens recebidos. Permite à tela
   * esconder o histórico quando não há nenhum registro.
   */
  aoCarregar?: (quantidade: number) => void
}

export const ROTULOS_CATEGORIA: Record<CategoriaAuditoria, string> = {
  Situacao: 'Situação',
  Prorrogacao: 'Prorrogação',
  Transferencia: 'Transferência',
  TCC: 'TCC',
  Recuperacao: 'Recuperação',
  Contrato: 'Contrato',
  Rescisao: 'Rescisão',
  MatriculaNegociada: 'Matrícula negociada',
  Precificacao: 'Precificação',
  Observacao: 'Observação',
  Financeiro: 'Financeiro',
  Reingresso: 'Reingresso',
  Turma: 'Turma'
}

export const OPCOES_CATEGORIA_ALUNO: OpcaoCategoriaAuditoria[] = [
  { categoria: 'Situacao', texto: 'Situação' },
  { categoria: 'Prorrogacao', texto: 'Prorrogação' },
  { categoria: 'Transferencia', texto: 'Transferência' },
  { categoria: 'TCC', texto: 'TCC' },
  { categoria: 'Recuperacao', texto: 'Recuperação' },
  { categoria: 'Contrato', texto: 'Contrato' },
  { categoria: 'Financeiro', texto: 'Financeiro' },
  { categoria: 'Reingresso', texto: 'Reingresso' },
  { categoria: 'Observacao', texto: 'Observação' }
]

/**
 * Data (yyyy-MM-dd) em que a auditoria passou a registrar alterações. Ajustar para a data do
 * deploy em produção. Alterações anteriores a ela não constam no histórico.
 */
export const AUDITORIA_INICIO_REGISTROS = '2026-10-14'

export const formatarInicioAuditoria = (): string => {
  const [ano, mes, dia] = AUDITORIA_INICIO_REGISTROS.split('-')
  return `${dia}/${mes}/${ano}`
}

const CAMPOS_PLANO_PRECIFICACAO = ['PlanoPagamento', 'ValorMatricula', 'Curso']

const vazio = (valor: string | null | undefined): boolean =>
  valor === null || valor === undefined || valor.trim() === ''

/**
 * Texto da alteração de um campo. Na precificação, um valor vazio significa
 * plano incluído (anterior vazio) ou excluído (novo vazio).
 */
export const descreverAlteracao = (
  alteracao: AlteracaoAuditoriaDto,
  contexto: ContextoAuditoriaDto
): string => {
  const { campoDescricao, campo, valorAnterior, valorNovo } = alteracao

  if (
    contexto.tipoEntidade === 'Precificacao' &&
    CAMPOS_PLANO_PRECIFICACAO.includes(campo)
  ) {
    if (vazio(valorAnterior) && !vazio(valorNovo)) {
      return `${campoDescricao}: incluído — ${valorNovo}`
    }
    if (!vazio(valorAnterior) && vazio(valorNovo)) {
      return `${campoDescricao}: excluído — ${valorAnterior}`
    }
  }

  const anterior = vazio(valorAnterior) ? '(vazio)' : valorAnterior
  const novo = vazio(valorNovo) ? '(vazio)' : valorNovo

  return `${campoDescricao}: ${anterior} → ${novo}`
}

export const descreverUsuario = (
  usuario: string,
  impersonadoPor: string | null
): string =>
  impersonadoPor ? `${usuario} (impersonado por ${impersonadoPor})` : usuario
